import React, { useState, useEffect } from 'react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { Card, CardContent, CardHeader } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Heart, MessageCircle, Search, PenSquare, Clock, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import RegisterPopup from '../components/RegisterPopup';

const API_BASE = process.env.REACT_APP_API_URL || "http://127.0.0.1:8000";

const Community = () => {
  const { isLoggedIn } = useAuth();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [newPost, setNewPost] = useState({ title: '', content: '', category: '' });
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [showRegisterPopup, setShowRegisterPopup] = useState(false);

  // Fetch posts from database
  const fetchPosts = async (category = null) => {
    try {
      setLoading(true);
      let url = `${API_BASE}/posts`;
      if (category && category !== 'Tất cả') {
        url += `?category=${encodeURIComponent(category)}`;
      }
      const res = await fetch(url);
      if (res.ok) {
        const data = await res.json();
        setPosts(data);
      }
    } catch (error) {
      console.error("Error fetching posts:", error);
      toast.error("Không thể tải bài viết");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPosts();
  }, []);

  const categories = ['Tất cả', 'Tam cá nguyệt 1', 'Tam cá nguyệt 2', 'Tam cá nguyệt 3', 'Dinh dưỡng', 'Chuẩn bị sinh'];
  const [selectedCategory, setSelectedCategory] = useState('Tất cả');

  const filteredPosts = posts.filter(post => {
    const matchesSearch = post.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         post.content.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesSearch;
  });

  const handleCategoryChange = (category) => {
    setSelectedCategory(category);
    fetchPosts(category);
  };

  const handleLike = async (postId) => {
    const userId = localStorage.getItem("user_id");
    if (!userId) {
      toast.error("Vui lòng đăng nhập để thích bài viết");
      return;
    }

    try {
      const res = await fetch(`${API_BASE}/posts/${postId}/like?user_id=${userId}`, {
        method: "POST"
      });
      if (res.ok) {
        const data = await res.json();
        setPosts(posts.map(post => 
          post.id === postId ? { ...post, likes: data.likes } : post
        ));
        toast.success(data.message === "Post liked" ? 'Đã thích bài viết!' : 'Đã bỏ thích bài viết!');
      }
    } catch (error) {
      console.error("Error liking post:", error);
      toast.error("Không thể thích bài viết");
    }
  };

  const handleCreatePost = async () => {
    if (!newPost.title || !newPost.content) {
      toast.error('Vui lòng điền đầy đủ thông tin!');
      return;
    }

    const userId = localStorage.getItem("user_id");
    if (!userId) {
      toast.error("Vui lòng đăng nhập để đăng bài");
      return;
    }

    try {
      const res = await fetch(`${API_BASE}/posts`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          user_id: parseInt(userId),
          title: newPost.title,
          content: newPost.content,
          category: newPost.category || null
        })
      });

      if (res.ok) {
        const data = await res.json();
        setPosts([data.post, ...posts]);
        setNewPost({ title: '', content: '', category: '' });
        setIsDialogOpen(false);
        toast.success('Đăng bài thành công!');
      } else {
        toast.error('Không thể đăng bài');
      }
    } catch (error) {
      console.error("Error creating post:", error);
      toast.error('Lỗi kết nối máy chủ');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-pink-50 via-white to-pink-50">
      <Navbar />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-5xl md:text-6xl font-bold mb-4">
            <span className="bg-gradient-to-r from-pink-500 to-pink-700 bg-clip-text text-transparent">
              Cộng đồng mẹ bầu
            </span>
          </h1>
          <p className="text-2xl text-gray-600">
            Kết nối, chia sẻ và học hỏi từ nhau
          </p>
        </div>

        {/* Search and Create */}
        <div className="mb-8 flex flex-col sm:flex-row gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <Input
              placeholder="Tìm kiếm bài viết..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 border-pink-200 focus:border-pink-400 h-12"
            />
          </div>
          {isLoggedIn ? (
            <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
              <DialogTrigger asChild>
                <Button className="bg-gradient-to-r from-pink-500 to-pink-600 hover:from-pink-600 hover:to-pink-700 text-white h-12 px-6">
                  <PenSquare className="w-5 h-5 mr-2" />
                  Viết bài mới
                </Button>
              </DialogTrigger>
            <DialogContent className="max-w-2xl">
              <DialogHeader>
                <DialogTitle className="text-2xl text-gray-800">Tạo bài viết mới</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 mt-4">
                <Input
                  placeholder="Tiêu đề bài viết"
                  value={newPost.title}
                  onChange={(e) => setNewPost({ ...newPost, title: e.target.value })}
                  className="border-pink-200 focus:border-pink-400"
                />
                <select
                  value={newPost.category}
                  onChange={(e) => setNewPost({ ...newPost, category: e.target.value })}
                  className="w-full p-2 border border-pink-200 rounded-md focus:outline-none focus:border-pink-400"
                >
                  <option value="">Chọn danh mục</option>
                  {categories.filter(c => c !== 'Tất cả').map(cat => (
                    <option key={cat} value={cat}>{cat}</option>
                  ))}
                </select>
                <Textarea
                  placeholder="Nội dung bài viết..."
                  value={newPost.content}
                  onChange={(e) => setNewPost({ ...newPost, content: e.target.value })}
                  rows={6}
                  className="border-pink-200 focus:border-pink-400"
                />
                <div className="flex justify-end space-x-3">
                  <Button variant="outline" onClick={() => setIsDialogOpen(false)}>
                    Hủy
                  </Button>
                  <Button
                    onClick={handleCreatePost}
                    className="bg-gradient-to-r from-pink-500 to-pink-600 hover:from-pink-600 hover:to-pink-700 text-white"
                  >
                    Đăng bài
                  </Button>
                </div>
              </div>
            </DialogContent>
            </Dialog>
          ) : (
            <Button 
              className="bg-gradient-to-r from-pink-500 to-pink-600 hover:from-pink-600 hover:to-pink-700 text-white h-12 px-6"
              onClick={() => setShowRegisterPopup(true)}
            >
              <PenSquare className="w-5 h-5 mr-2" />
              Viết bài mới
            </Button>
          )}
        </div>

        {/* Loading */}
        {loading && (
          <div className="flex justify-center items-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-pink-500" />
            <span className="ml-2 text-gray-600">Đang tải bài viết...</span>
          </div>
        )}

        {/* Categories */}
        <div className="mb-8 flex flex-wrap gap-2">
          {categories.map((category) => (
            <Button
              key={category}
              variant={selectedCategory === category ? 'default' : 'outline'}
              onClick={() => handleCategoryChange(category)}
              className={selectedCategory === category
                ? 'bg-gradient-to-r from-pink-500 to-pink-600 text-white'
                : 'border-pink-200 text-gray-700 hover:bg-pink-50 hover:text-pink-600'
              }
            >
              {category}
            </Button>
          ))}
        </div>

        {/* Posts */}
        <div className="space-y-6">
          {!loading && filteredPosts.length > 0 ? (
            filteredPosts.map((post) => (
              <Card key={post.id} className="border-pink-100 shadow-md hover:shadow-xl transition-all duration-300">
                <CardHeader>
                  <div className="flex items-start justify-between">
                    <div className="flex items-center space-x-4">
                      <Avatar className="w-12 h-12 border-2 border-pink-200">
                        <AvatarImage src={post.avatar} />
                        <AvatarFallback className="bg-pink-100 text-pink-600">
                          {post.author.charAt(0)}
                        </AvatarFallback>
                      </Avatar>
                      <div>
                        <div className="flex items-center space-x-2">
                          <h3 className="font-semibold text-gray-800">{post.author}</h3>
                          <Badge className="bg-pink-100 text-pink-700 hover:bg-pink-200">
                            Tuần {post.week}
                          </Badge>
                        </div>
                        <div className="flex items-center text-sm text-gray-500 mt-1">
                          <Clock className="w-4 h-4 mr-1" />
                          {post.time}
                        </div>
                      </div>
                    </div>
                    <Badge variant="outline" className="border-pink-300 text-pink-600">
                      {post.category}
                    </Badge>
                  </div>
                </CardHeader>
                <CardContent>
                  <h2 className="text-xl font-semibold text-gray-800 mb-3">{post.title}</h2>
                  <p className="text-gray-700 leading-relaxed mb-4">{post.content}</p>
                  <div className="flex items-center space-x-6 pt-4 border-t border-pink-100">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleLike(post.id)}
                      className="text-gray-600 hover:text-pink-600 hover:bg-pink-50"
                    >
                      <Heart className="w-5 h-5 mr-2" />
                      {post.likes} Thích
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-gray-600 hover:text-pink-600 hover:bg-pink-50"
                    >
                      <MessageCircle className="w-5 h-5 mr-2" />
                      {post.comments_count || 0} Bình luận
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))
          ) : (
            <Card className="border-pink-100 p-12">
              <div className="text-center text-gray-500">
                <MessageCircle className="w-16 h-16 mx-auto mb-4 text-pink-300" />
                <p className="text-lg">Không tìm thấy bài viết nào</p>
              </div>
            </Card>
          )}
        </div>
      </div>

      <Footer />

      {/* Register Popup */}
      <RegisterPopup isOpen={showRegisterPopup} onClose={() => setShowRegisterPopup(false)} />
    </div>
  );
};

export default Community;